import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const ContactContainer = styled(Box)(({ theme }) => ({
  padding: '6rem 0',
  backgroundColor: 'white',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '1rem',
  color: '#1e293b',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  textAlign: 'center',
  marginBottom: '4rem',
  color: '#64748b',
  maxWidth: '600px',
  margin: '0 auto 4rem auto'
}));

const ContactCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  border: '1px solid #e2e8f0',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    '& .contact-icon': {
      transform: 'scale(1.1)',
      backgroundColor: '#1e40af'
    }
  }
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  backgroundColor: '#3b82f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '2rem',
  color: 'white',
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 16px rgba(59, 130, 246, 0.3)'
}));

const ContactTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: '1rem',
  color: '#1e293b'
}));

const ContactInfo = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#64748b',
  lineHeight: 1.7
}));

const CTASection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: '4rem',
  padding: '3rem',
  backgroundColor: '#f8fafc',
  borderRadius: '24px',
  border: '1px solid #e2e8f0'
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: '1rem',
  color: '#1e293b'
}));

const CTADescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: '#64748b',
  marginBottom: '2rem',
  maxWidth: '500px',
  margin: '0 auto 2rem auto'
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3b82f6',
  color: 'white',
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#2563eb',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)'
  }
}));

const ContactSection = ({ contact }) => {
  const {
    phone = '(555) 123-4567',
    email = 'contact@yourbusiness.com',
    address = '123 Business Street, City, State 12345',
    hours = 'Mon - Fri: 9:00 AM - 6:00 PM, Sat: 10:00 AM - 4:00 PM, Sun: Closed'
  } = contact || {};

  const contactItems = [
    {
      icon: <PhoneIcon fontSize="large" />,
      title: 'Phone',
      info: phone
    },
    {
      icon: <EmailIcon fontSize="large" />,
      title: 'Email',
      info: email
    },
    {
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Address',
      info: address
    },
    {
      icon: <AccessTimeIcon fontSize="large" />,
      title: 'Business Hours',
      info: hours
    }
  ];

  return (
    <ContactContainer>
      <Container maxWidth="lg">
        <SectionTitle variant="h2">
          Get In Touch
        </SectionTitle>
        <SectionSubtitle variant="body1">
          Ready to start your project? Contact us today for a free consultation
        </SectionSubtitle>
        <Grid container spacing={4}>
          {contactItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <ContactCard>
                <CardContent sx={{ padding: '2.5rem', textAlign: 'center' }}>
                  <ContactIcon className="contact-icon">
                    {item.icon}
                  </ContactIcon>
                  <ContactTitle variant="h6">
                    {item.title}
                  </ContactTitle>
                  <ContactInfo variant="body2">
                    {item.info}
                  </ContactInfo>
                </CardContent>
              </ContactCard>
            </Grid>
          ))}
        </Grid>
        <CTASection>
          <CTATitle variant="h3">
            Ready to Get Started?
          </CTATitle>
          <CTADescription variant="body1">
            Let's discuss your project and see how we can help you achieve your business goals.
          </CTADescription>
          <CTAButton variant="contained" size="large">
            Schedule a Consultation
          </CTAButton>
        </CTASection>
      </Container>
    </ContactContainer>
  );
};

export default ContactSection;
